import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { FieldTooltip } from '@/components/FieldLabel'
import { cn } from '@/lib/utils'

export interface InlineSelectOption<T extends string> {
  value: T
  label: string
}

interface InlineSelectFieldProps<T extends string> {
  /** Name of the setting, shown beside the control rather than above it. */
  label: string
  /** Explanatory help, hidden behind a hover icon next to the label. */
  tooltip?: string
  value: T
  options: InlineSelectOption<T>[]
  onChange: (value: T) => void
  /** Width of the select, as a Tailwind class. */
  triggerClassName?: string
  className?: string
}

/**
 * A select with its label on the same row, for places where a field sits in a
 * strip of controls — a card footer or a toolbar — instead of in a form column.
 */
export function InlineSelectField<T extends string>({
  label,
  tooltip,
  value,
  options,
  onChange,
  triggerClassName,
  className,
}: InlineSelectFieldProps<T>) {
  const selected = options.find((o) => o.value === value)

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className="flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap text-(--ad-text-muted)">
        {label}
        {tooltip && <FieldTooltip text={tooltip} />}
      </span>
      <Select label={label} value={value} onValueChange={(v) => onChange(v as T)}>
        <SelectTrigger size="sm" className={cn('w-44', triggerClassName)}>
          <SelectValue>{selected?.label ?? value}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
